import React, { useEffect, useRef, useState } from "react"
import AuthService from "../../services/auth.service"
import PhoneField from "../../components/phoneField"

/*
	Acceso de alumnos (Esteban, 2026-10-21; docs/ACCESO_ALUMNOS_CONTRATO.md). Tres piezas de la pantalla /access:

	- EntrarConWhatsApp: número (MX/HN con el mismo phoneField del resto de WAPP) → código de 6 dígitos → adentro.
	  Si la cuenta no tiene contraseña (o viene de "Olvidé mi contraseña") pasa a CrearClave antes de entrar.
	- CrearClave: "Crea Tu Contraseña", con "Más Tarde" cuando es opcional.
	- Invitacion: el enlace personal ?invitacion=<token>. "Hola Ana" → Crea Tu Contraseña → adentro. Si ya se usó o
	  venció, manda a EntrarConWhatsApp con su número ya escrito: nunca un callejón sin salida (gente poco técnica).
*/

const textoDe = (x: any, porDefecto: string) => x?.response?.data?.message || porDefecto

// Sin sesión no hay plantel: el país del selector sale de la zona horaria del navegador (Honduras o México).
const paisDelNavegador = (): number => {
	try {
		const zona = Intl.DateTimeFormat().resolvedOptions().timeZone || ""
		return /Tegucigalpa|El_Salvador|Guatemala|Managua/.test(zona) ? 2 : 1
	} catch (_) {
		return 1
	}
}

type ClaveProps = {
	titulo?: string
	saludo?: string
	texto?: string
	ocupadoExterno?: boolean
	alGuardar: (clave: string, otra: string) => Promise<void>
	alMasTarde?: () => void
}

export const CrearClave: React.FC<ClaveProps> = ({ titulo, saludo, texto, alGuardar, alMasTarde }) => {
	const [clave, setClave] = useState("")
	const [otra, setOtra] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [ocupado, setOcupado] = useState(false)
	const [ver, setVer] = useState(false)

	const guardar = async (e: React.FormEvent) => {
		e.preventDefault()
		if (clave.length < 8) { setError("Usa al menos 8 caracteres."); return }
		if (clave !== otra) { setError("Las dos contraseñas no coinciden."); return }
		setOcupado(true); setError(null)
		try {
			await alGuardar(clave, otra)
		} catch (x: any) {
			setError(textoDe(x, "No se pudo guardar. Revisa tu conexión e intenta otra vez."))
			setOcupado(false)
		}
	}

	return (
		<form className="form accForm" onSubmit={guardar} noValidate>
			{saludo ? <h1 className="pageHeader accSaludo">{saludo}</h1> : null}
			<h2 className="h1">{titulo || "Crea Tu Contraseña"}</h2>
			<p>{texto || "La próxima vez entras con tu número de WhatsApp (o tu correo) y esta contraseña."}</p>
			<div className="divInput">
				<input placeholder="Contraseña (mínimo 8)" type={ver ? "text" : "password"} autoComplete="new-password" maxLength={50}
					value={clave} disabled={ocupado} onChange={(e) => setClave(e.target.value)} autoFocus />
			</div>
			<div className="divInput">
				<input placeholder="Repítela" type={ver ? "text" : "password"} autoComplete="new-password" maxLength={50}
					value={otra} disabled={ocupado} onChange={(e) => setOtra(e.target.value)} />
			</div>
			<button type="button" className="accEnlace accVer" onClick={() => setVer(!ver)}>{ver ? "Ocultar Contraseña" : "Mostrar Contraseña"}</button>
			{error ? <p className="accError" role="alert">{error}</p> : null}
			<div className="accBotones">
				<button className="btn btnPurple rounded-pill" type="submit" disabled={ocupado}>{ocupado ? "Guardando…" : "Guardar Y Entrar"}</button>
				{alMasTarde ? <button className="accEnlace" type="button" disabled={ocupado} onClick={alMasTarde}>Más Tarde</button> : null}
			</div>
		</form>
	)
}

/** Primer nombre de quien entró, para "Entraste Como Ana" (lo manda el servidor; si no, de la sesión). */
const nombreDe = (d: any): string => String(d?.nombre || String(d?.user?.first_name || "").trim().split(/\s+/)[0] || "")

export type Seleccion = { boleto: string, cuentas: { id: number, nombre: string }[] }

/*
	Varias cuentas con el mismo número (hermanos, fichas duplicadas; revisión de seguridad 2026-10-21): después del
	código o de la contraseña, la persona escoge cuál es, solo por el primer nombre.
*/
export const ElegirCuenta: React.FC<{ seleccion: Seleccion, alEntrar: (data: any) => void, alVolver: () => void }> = ({ seleccion, alEntrar, alVolver }) => {
	const [ocupado, setOcupado] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const elegir = async (id: number) => {
		setOcupado(true); setError(null)
		try {
			const r = await AuthService.whatsappElegir(seleccion.boleto, id)
			alEntrar(r.data)
		} catch (x: any) {
			setError(textoDe(x, "No se pudo entrar. Revisa tu conexión e intenta otra vez."))
			setOcupado(false)
		}
	}
	return (
		<div className="form accForm">
			<h2 className="h1">¿Quién Eres?</h2>
			<p>Este número tiene más de una cuenta. Escoge la tuya.</p>
			<div className="accCuentas">
				{seleccion.cuentas.map((c) => (
					<button key={c.id} type="button" className="accBotonWa" disabled={ocupado} onClick={() => elegir(c.id)}>{c.nombre}</button>
				))}
			</div>
			{error ? <p className="accError" role="alert">{error}</p> : null}
			<div className="accEnlaces">
				<button type="button" className="accEnlace" disabled={ocupado} onClick={alVolver}>Volver</button>
			</div>
		</div>
	)
}

type WaProps = {
	/** "entrar" (botón del login) u "olvide" (siempre pide la contraseña nueva al final). */
	modo: "entrar" | "olvide"
	telefonoInicial?: string
	paisInicial?: "mx" | "hn"
	avisoInicial?: string
	alEntrar: (data: any) => void
	alVolver: () => void
}

export const EntrarConWhatsApp: React.FC<WaProps> = ({ modo, telefonoInicial, paisInicial, avisoInicial, alEntrar, alVolver }) => {
	const prefijo = paisInicial === "hn" ? "504" : paisInicial === "mx" ? "52" : ""
	const [telefono, setTelefono] = useState<string>(telefonoInicial ? prefijo + telefonoInicial : "")
	const [paso, setPaso] = useState<"numero" | "codigo" | "elegir" | "clave">("numero")
	const [seleccion, setSeleccion] = useState<Seleccion | null>(null)
	const [codigo, setCodigo] = useState("")
	const [aviso, setAviso] = useState<string | null>(avisoInicial || null)
	const [error, setError] = useState<string | null>(null)
	const [ocupado, setOcupado] = useState(false)
	const [datos, setDatos] = useState<any>(null)
	const enviando = useRef(false)
	const campoCodigo = useRef<HTMLInputElement>(null)
	const paisCampo = paisInicial === "hn" ? 2 : paisInicial === "mx" ? 1 : paisDelNavegador()

	//Número completo según su país: México 10 dígitos (con o sin el 1), Honduras 8 (QA 2026-10-21: un 504 + 7 pasaba).
	const completo = (): boolean => {
		const d = telefono.replace(/\D/g, "")
		if (d.startsWith("504")) return d.length === 11
		if (d.startsWith("521")) return d.length === 13
		if (d.startsWith("52")) return d.length === 12
		return false
	}

	const pedir = async (e?: React.FormEvent) => {
		e && e.preventDefault()
		if (!completo()) { setError("Escribe tu número de WhatsApp completo: 10 dígitos en México u 8 en Honduras."); return }
		const otro = paso === "codigo"
		setOcupado(true); setError(null)
		try {
			const r = await AuthService.whatsappPedir(telefono)
			setAviso((otro ? "Te mandamos OTRO código" : "Te mandamos un código") + " por WhatsApp al número que termina en " + String(r.data.terminacion || "").replace(/\D/g, "") + "."
				+ (otro ? " Usa el más nuevo." : ""))
			setCodigo("")
			setPaso("codigo")
		} catch (x: any) {
			setError(textoDe(x, "No se pudo mandar el código. Revisa tu conexión e intenta otra vez."))
		} finally {
			setOcupado(false)
		}
	}

	const confirmar = async (valor: string) => {
		if (enviando.current || valor.length !== 6) return
		enviando.current = true
		setOcupado(true); setError(null)
		try {
			const r = await AuthService.whatsappEntrar(telefono, valor)
			if (r.data.elegir) {
				setSeleccion({ boleto: r.data.boleto, cuentas: r.data.cuentas || [] })
				setPaso("elegir")
			} else {
				seguir(r.data)
			}
		} catch (x: any) {
			setError(textoDe(x, "No se pudo entrar. Revisa tu conexión e intenta otra vez."))
			setCodigo("")
			//El campo no se deshabilita: en el celular el teclado se cerraba tras un código malo.
			setTimeout(() => campoCodigo.current?.focus(), 0)
		} finally {
			setOcupado(false)
			enviando.current = false
		}
	}

	//Ya con la cuenta: sin contraseña (o desde "Olvidé") pasa a crearla; si no, adentro.
	const seguir = (data: any) => {
		if (data.sinClave || modo === "olvide") {
			setDatos(data)
			setPaso("clave")
		} else {
			alEntrar(data)
		}
	}

	useEffect(() => { if (codigo.length === 6) confirmar(codigo) }, [codigo])

	if (paso === "elegir" && seleccion) {
		return <ElegirCuenta seleccion={seleccion} alEntrar={seguir} alVolver={() => { setPaso("numero"); setSeleccion(null) }} />
	}

	if (paso === "clave") {
		return <CrearClave
			saludo={nombreDe(datos) ? "Entraste Como " + nombreDe(datos) : undefined}
			titulo={modo === "olvide" ? "Crea Tu Nueva Contraseña" : "Crea Tu Contraseña"}
			texto={modo === "olvide" ? "Ya entraste. Escribe la contraseña que vas a usar de ahora en adelante." : "La próxima vez entras con tu número de WhatsApp (o tu correo) y esta contraseña."}
			alGuardar={async (clave, otra) => {
				try {
					await AuthService.cambiarClave(clave, otra, undefined, undefined, datos.accessToken)
				} catch (x: any) {
					//Olvidé: si escribe la misma de antes, la recordó; se entra sin más.
					if (!(modo === "olvide" && x?.response?.data?.error_code === "clave_igual")) throw x
				}
				alEntrar(datos)
			}}
			alMasTarde={modo === "olvide" ? undefined : () => alEntrar(datos)}
		/>
	}

	if (paso === "codigo") {
		return (
			<form className="form accForm" onSubmit={(e) => { e.preventDefault(); confirmar(codigo) }}>
				<h2 className="h1">Escribe Tu Código</h2>
				{aviso ? <p>{aviso}</p> : null}
				<div className="divInput">
					<input ref={campoCodigo} className="accCodigo" placeholder="6 dígitos" inputMode="numeric" autoComplete="one-time-code" maxLength={14}
						value={codigo} readOnly={ocupado} autoFocus aria-label="Código de 6 dígitos"
						onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))} />
				</div>
				{error ? <p className="accError" role="alert">{error}</p> : null}
				<div className="accBotones">
					<button className="btn btnPurple rounded-pill" type="submit" disabled={ocupado || codigo.length !== 6}>{ocupado ? "Entrando…" : "Entrar"}</button>
				</div>
				<div className="accEnlaces">
					<button type="button" className="accEnlace" disabled={ocupado} onClick={() => pedir()}>Mandar Otro Código</button>
					<button type="button" className="accEnlace" disabled={ocupado} onClick={() => { setPaso("numero"); setError(null); setAviso(null) }}>Cambiar Número</button>
				</div>
			</form>
		)
	}

	return (
		<form className="form accForm" onSubmit={pedir} noValidate>
			<h2 className="h1">{modo === "olvide" ? "Olvidé Mi Contraseña" : "Entrar Con WhatsApp"}</h2>
			<p>{aviso || (modo === "olvide" ? "Te mandamos un código a tu WhatsApp para entrar y crear una nueva." : "Escribe tu número y te mandamos un código.")}</p>
			<div className="divInput accTelefono">
				<PhoneField id="accTelefono" name="telefono" value={telefono} campusCountryId={paisCampo} disabled={ocupado}
					onChange={(v) => setTelefono(v)} onEnter={() => pedir()} required />
			</div>
			{error ? <p className="accError" role="alert">{error}</p> : null}
			<div className="accBotones">
				<button className="btn btnPurple rounded-pill" type="submit" disabled={ocupado}>{ocupado ? "Enviando…" : "Mándame Un Código"}</button>
			</div>
			<div className="accEnlaces">
				<button type="button" className="accEnlace" disabled={ocupado} onClick={alVolver}>Entrar Con Contraseña</button>
			</div>
		</form>
	)
}

type InvProps = {
	token: string
	alEntrar: (data: any) => void
	/** Usada / vencida / inválida: a "Entrar Con WhatsApp" con el número escrito (si lo hay) y el motivo. */
	alNoSirve: (aviso: string, telefono?: string, pais?: "mx" | "hn") => void
}

export const Invitacion: React.FC<InvProps> = ({ token, alEntrar, alNoSirve }) => {
	const [nombre, setNombre] = useState<string | null>(null)
	const [falla, setFalla] = useState<string | null>(null)

	const noSirve = (x: any) => {
		const d = x?.response?.data
		if (x?.response?.status === 410) {
			alNoSirve(d?.message || "Este enlace ya no sirve. Entra con WhatsApp.", d?.telefono, d?.pais)
		} else {
			setFalla("No pudimos abrir tu invitación. Revisa tu conexión y vuelve a abrir el enlace.")
		}
	}

	useEffect(() => {
		let vivo = true
		AuthService.invitacion(token).then((r) => { if (vivo) setNombre(r.data.nombre || "") }, (x) => { if (vivo) noSirve(x) })
		return () => { vivo = false }
	}, [token])

	if (falla) return <div className="form accForm"><h2 className="h1">Tu Invitación</h2><p className="accError" role="alert">{falla}</p></div>
	if (nombre === null) return <div className="form accForm"><p>Abriendo tu invitación…</p></div>

	return <CrearClave
		saludo={nombre ? "Hola " + nombre : "Hola"}
		texto="La vas a usar para entrar a tu portal desde cualquier lugar."
		alGuardar={async (clave, otra) => {
			try {
				const r = await AuthService.invitacionAceptar(token, clave, otra)
				alEntrar(r.data)
			} catch (x: any) {
				if (x?.response?.status === 410) { noSirve(x); return }
				throw x
			}
		}}
	/>
}
